export type AclFeature =
  | 'dashboard'
  | 'students'
  | 'teachers'
  | 'attendance'
  | 'staffAttendance'
  | 'caseload'
  | 'scheduling'
  | 'sessions'
  | 'assessments'
  | 'progress'
  | 'approvals'
  | 'reports'
  | 'appointments'
  | 'resources'
  | 'accessControl'
  | 'settings'
  | 'announcements'
  | 'schoolCalendar'
  | 'lessonPlan';

export type AclPermission = 'view' | 'create' | 'edit' | 'delete' | 'manage';

export type RoleAclMap = Record<string, Partial<Record<AclFeature, AclPermission[]>>>;

export const ALL_FEATURES: { feature: AclFeature; labelKey: string; icon: string }[] = [
  { feature: 'dashboard',       labelKey: 'common.dashboard',               icon: 'ri-dashboard-3-line' },
  { feature: 'students',        labelKey: 'students.pageTitle',            icon: 'ri-team-line' },
  { feature: 'teachers',        labelKey: 'teachers.pageTitle',            icon: 'ri-user-settings-line' },
  { feature: 'attendance',      labelKey: 'attendance.pageTitle',          icon: 'ri-calendar-check-line' },
  { feature: 'staffAttendance', labelKey: 'attendance.staffAttendance',    icon: 'ri-user-follow-line' },
  { feature: 'caseload',        labelKey: 'dashboard.caseload',            icon: 'ri-folder-user-line' },
  { feature: 'scheduling',      labelKey: 'dashboard.scheduling',          icon: 'ri-calendar-schedule-line' },
  { feature: 'sessions',        labelKey: 'dashboard.sessions',            icon: 'ri-stethoscope-line' },
  { feature: 'assessments',     labelKey: 'dashboard.assessments',         icon: 'ri-file-chart-line' },
  { feature: 'progress',        labelKey: 'dashboard.progress',            icon: 'ri-line-chart-line' },
  { feature: 'approvals',       labelKey: 'dashboard.approvals',           icon: 'ri-checkbox-circle-line' },
  { feature: 'reports',         labelKey: 'dashboard.reports',             icon: 'ri-bar-chart-grouped-line' },
  { feature: 'appointments',    labelKey: 'dashboard.appointments',        icon: 'ri-calendar-check-line' },
  { feature: 'resources',       labelKey: 'dashboard.resources',           icon: 'ri-book-open-line' },
  { feature: 'accessControl',   labelKey: 'dashboard.accessControl',       icon: 'ri-shield-check-line' },
  { feature: 'settings',        labelKey: 'common.settings',               icon: 'ri-settings-4-line' },
  { feature: 'announcements',   labelKey: 'dashboard.announcements',       icon: 'ri-megaphone-line' },
  { feature: 'schoolCalendar',  labelKey: 'dashboard.schoolCalendar',      icon: 'ri-calendar-2-line' },
  { feature: 'lessonPlan',     labelKey: 'lessonPlans.pageTitle',          icon: 'ri-file-list-3-line' },
];

export const ALL_PERMISSIONS: { perm: AclPermission; labelKey: string }[] = [
  { perm: 'view',   labelKey: 'acl.permView' },
  { perm: 'create', labelKey: 'acl.permCreate' },
  { perm: 'edit',   labelKey: 'acl.permEdit' },
  { perm: 'delete', labelKey: 'acl.permDelete' },
  { perm: 'manage', labelKey: 'acl.permManage' },
];

// ── Default ACL mapping — role → feature → permissions ──
export const defaultAcl: RoleAclMap = {
  'Super Admin': {
    dashboard:       ['view', 'manage'],
    students:        ['view', 'create', 'edit', 'delete', 'manage'],
    teachers:        ['view', 'create', 'edit', 'delete', 'manage'],
    attendance:      ['view', 'create', 'edit', 'manage'],
    staffAttendance: ['view', 'create', 'edit', 'manage'],
    caseload:        ['view', 'manage'],
    scheduling:      ['view', 'create', 'edit', 'delete', 'manage'],
    sessions:        ['view', 'create', 'edit', 'manage'],
    assessments:     ['view', 'create', 'edit', 'delete', 'manage'],
    progress:        ['view', 'manage'],
    approvals:       ['view', 'edit', 'manage'],
    reports:         ['view', 'create', 'manage'],
    appointments:    ['view', 'create', 'edit', 'manage'],
    resources:       ['view', 'create', 'edit', 'manage'],
    accessControl:   ['view', 'create', 'edit', 'delete', 'manage'],
    settings:        ['view', 'edit', 'manage'],
    announcements:   ['view', 'create', 'edit', 'delete', 'manage'],
    schoolCalendar:  ['view', 'create', 'edit', 'delete', 'manage'],
    lessonPlan:      ['view', 'create', 'edit', 'delete', 'manage'],
  },
  'Admin': {
    dashboard:       ['view', 'manage'],
    students:        ['view', 'create', 'edit', 'delete', 'manage'],
    teachers:        ['view', 'create', 'edit', 'delete', 'manage'],
    attendance:      ['view', 'create', 'edit', 'manage'],
    staffAttendance: ['view', 'create', 'edit', 'manage'],
    caseload:        ['view'],
    scheduling:      ['view', 'create', 'edit', 'manage'],
    sessions:        ['view'],
    assessments:     ['view', 'create', 'edit', 'delete', 'manage'],
    progress:        ['view'],
    approvals:       ['view', 'edit', 'manage'],
    reports:         ['view', 'create', 'manage'],
    appointments:    ['view', 'create', 'edit'],
    resources:       ['view', 'create', 'edit'],
    accessControl:   ['view', 'edit'],
    settings:        ['view', 'edit', 'manage'],
    announcements:   ['view', 'create', 'edit', 'delete', 'manage'],
    schoolCalendar:  ['view', 'create', 'edit', 'delete', 'manage'],
    lessonPlan:      ['view', 'create', 'edit', 'delete', 'manage'],
  },
  'Coordinator': {
    dashboard:       ['view'],
    students:        ['view', 'create', 'edit'],
    teachers:        ['view', 'create', 'edit'],
    attendance:      ['view'],
    staffAttendance: ['view'],
    caseload:        ['view'],
    scheduling:      ['view', 'create', 'edit'],
    sessions:        [],
    assessments:     ['view'],
    progress:        ['view'],
    approvals:       ['view', 'edit'],
    reports:         ['view'],
    appointments:    ['view', 'create', 'edit'],
    resources:       ['view'],
    accessControl:   [],
    settings:        [],
    announcements:   ['view', 'create', 'edit', 'delete'],
    schoolCalendar:  ['view', 'create', 'edit'],
  },
  'Teacher': {
    dashboard:       ['view'],
    students:        ['view', 'edit'],
    teachers:        [],
    attendance:      ['view'],
    staffAttendance: [],
    caseload:        [],
    scheduling:      [],
    sessions:        [],
    assessments:     ['view'],
    progress:        ['view', 'edit'],
    approvals:       [],
    reports:         [],
    appointments:    ['view'],
    resources:       ['view', 'create'],
    accessControl:   [],
    settings:        [],
    announcements:   ['view', 'create', 'edit'],
    schoolCalendar:  ['view', 'create', 'edit'],
    lessonPlan:      ['view', 'create', 'edit'],
  },
  'Therapist': {
    dashboard:       ['view'],
    students:        ['view'],
    teachers:        [],
    attendance:      [],
    staffAttendance: [],
    caseload:        ['view', 'edit', 'manage'],
    scheduling:      ['view'],
    sessions:        ['view', 'create', 'edit', 'delete'],
    assessments:     ['view', 'create', 'edit'],
    progress:        ['view', 'create', 'edit'],
    approvals:       [],
    reports:         [],
    appointments:    ['view'],
    resources:       ['view'],
    accessControl:   [],
    settings:        [],
    announcements:   ['view', 'create', 'edit'],
    schoolCalendar:  ['view', 'create', 'edit'],
  },
  'Parent': {
    dashboard:       ['view'],
    students:        [],
    teachers:        [],
    attendance:      [],
    staffAttendance: [],
    caseload:        [],
    scheduling:      [],
    sessions:        [],
    assessments:     [],
    progress:        ['view'],
    approvals:       [],
    reports:         [],
    appointments:    ['view', 'create'],
    resources:       ['view'],
    accessControl:   [],
    settings:        [],
    announcements:   [],
    schoolCalendar:  ['view'],
  },
  'Student': {
    dashboard:       ['view'],
    students:        [],
    teachers:        [],
    attendance:      [],
    staffAttendance: [],
    caseload:        [],
    scheduling:      [],
    sessions:        [],
    assessments:     [],
    progress:        ['view'],
    approvals:       [],
    reports:         [],
    appointments:    ['view'],
    resources:       ['view'],
    accessControl:   [],
    settings:        [],
    announcements:   [],
    schoolCalendar:  ['view'],
  },
  'Accountant': {
    dashboard:       ['view'],
    students:        [],
    teachers:        [],
    attendance:      [],
    staffAttendance: [],
    caseload:        [],
    scheduling:      [],
    sessions:        [],
    assessments:     [],
    progress:        [],
    approvals:       [],
    reports:         ['view'],
    appointments:    [],
    resources:       [],
    accessControl:   [],
    settings:        [],
    announcements:   [],
    schoolCalendar:  ['view'],
  },
  'Librarian': {
    dashboard:       ['view'],
    students:        [],
    teachers:        [],
    attendance:      [],
    staffAttendance: [],
    caseload:        [],
    scheduling:      [],
    sessions:        [],
    assessments:     [],
    progress:        [],
    approvals:       [],
    reports:         [],
    appointments:    [],
    resources:       ['view', 'create', 'edit'],
    accessControl:   [],
    settings:        [],
  },
  'Receptionist': {
    dashboard:       ['view'],
    students:        ['view'],
    teachers:        [],
    attendance:      [],
    staffAttendance: [],
    caseload:        [],
    scheduling:      [],
    sessions:        [],
    assessments:     [],
    progress:        [],
    approvals:       [],
    reports:         [],
    appointments:    ['view', 'create', 'edit'],
    resources:       [],
    accessControl:   [],
    settings:        [],
    announcements:   [],
    schoolCalendar:  ['view'],
  },
};

export function getPermissionsForRole(acl: RoleAclMap, role: string): Partial<Record<AclFeature, AclPermission[]>> {
  return acl[role] ?? {};
}

export function hasPermission(
  acl: RoleAclMap,
  role: string,
  feature: AclFeature,
  permission: AclPermission,
): boolean {
  const rolePerms = acl[role];
  if (!rolePerms || !rolePerms[feature]) return false;
  return rolePerms[feature]!.includes(permission);
}